import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import { APIGetTwitterLotteryPageByIdResponse } from '@/server/api/twitter_lottery_pages/get_lottery_page_by_id.api'
import { APIError } from '@/typings/api/error'

import { API } from '../utils/api'

export type Form = {
  campaignId?: number
  twCampaignPrizeId?: number
  twWebInstantwinLotteryPageSettingId?: number
  name: string
  lotteryType: 0 | 1
  lotteryText: string
  mediaType: 0 | 1
  mediaUrl: string | null
  mediaOriginName: string | null
}

export type TLotteryPageSetting = {
  id: number
  name: string
  previewUrl: string | null
  lotteryType: 0 | 1
}

export type FetchLotteryPageSettingsParams = {
  campaignId: number
  prizeId: number
  sort?: 'ASC' | 'DESC'
  pagingNo?: number
  limitCount?: number
}

export type LotteryPageMode = 'settings' | 'create' | 'edit_win' | 'edit_lose' | 'copy'

export type GetLotteryPageSettingsResponse = {
  data?: {
    lotteryPages: TLotteryPageSetting[]
    total: number
  }
  error?: APIError
}

export type GetLotteryPagesLotteryPagesResponse = {
  data: APIGetTwitterLotteryPageByIdResponse
  error: APIError
}

const initialForm: Form = {
  name: '',
  lotteryType: 0,
  lotteryText: '',
  mediaType: 0,
  mediaUrl: '',
  mediaOriginName: ''
}

const initialParams: FetchLotteryPageSettingsParams = {
  campaignId: 0,
  prizeId: 0,
  pagingNo: 1,
  limitCount: 25
}

export const useLotteryPageSettingDialogStore = defineStore('lotteryPageSettingDialogStore', () => {
  const lotteryPages = ref<TLotteryPageSetting[]>([])
  const form = ref<Form>({ ...initialForm })
  const error = ref(false)
  const mode = ref<LotteryPageMode>('settings')
  const total = ref(0)
  const params = ref<FetchLotteryPageSettingsParams>({ ...initialParams })
  const isLoading = ref(false)

  const reset = async (): Promise<void> => {
    lotteryPages.value = []
    total.value = 0
    error.value = false
    mode.value = 'settings'
    form.value = { ...initialForm }
    params.value = { ...initialParams }
  }

  const setParams = async (payload: Partial<FetchLotteryPageSettingsParams>): Promise<void> => {
    params.value = { ...params.value, ...payload }
  }

  const setForm = async (payload: Form): Promise<void> => {
    form.value = payload
  }

  const setMode = async (payload: LotteryPageMode): Promise<void> => {
    mode.value = payload
  }

  const fetchLotteryPages = async (): Promise<void> => {
    mode.value = 'settings'
    error.value = false

    const response = (await API.get('/twitter_lottery_pages', {
      params: params.value
    })) as GetLotteryPageSettingsResponse

    if (!response.data) {
      error.value = true
      return
    }

    lotteryPages.value = response.data.lotteryPages
    total.value = response.data.total
  }

  return {
    lotteryPages: readonly(lotteryPages),
    form: readonly(form),
    error: readonly(error),
    mode: readonly(mode),
    total: readonly(total),
    params: readonly(params),
    isLoading: readonly(isLoading),
    reset,
    setParams,
    setForm,
    setMode,
    fetchLotteryPages
  }
})
